/**
 * The customer vocabulary (Design Bible 10).
 *
 * The left column of the Bible's normative table is a database value; the
 * customer column is the *only* permitted surface string. This module is the
 * only place a system enum becomes words, so no view can leak `ACTIVE_WEBSITE`,
 * `REVIEW_REQUIRED`, or `PENDING_PAYMENT` to a collector, and no view can
 * invent a friendlier synonym either.
 *
 * 10.2 also bans a vocabulary: no `Oops`, no `Sorry`, no `Something went
 * wrong`, no emoji as a status channel. Every string below states a fact.
 */

import type {
  CheckoutStatus,
  HoldReason,
  PublicAvailability
} from "./publicContract.js";

export interface AvailabilityLabel {
  readonly label: string;
  /** 6.6: every state carries icon, text, and colour, in that priority order. */
  readonly tone: "ok" | "accent" | "neutral";
  readonly detail: string;
}

export function availabilityLabel(
  availability: PublicAvailability,
  checkoutEligible: boolean
): AvailabilityLabel {
  if (availability === "ACTIVE_EBAY") {
    return {
      label: "Buy on eBay",
      tone: "accent",
      detail: "This item is listed on eBay. Checkout happens there."
    };
  }
  if (!checkoutEligible) {
    // Core published the item but did not mark it checkout eligible. Design
    // Bible 10 gives `CHANNEL_SWITCHING` the customer label "Not available
    // right now", which is exactly what this is: published, not buyable here.
    return {
      label: "Not available right now",
      tone: "neutral",
      detail: "This item is not available for direct checkout at the moment."
    };
  }
  return {
    label: "Available",
    tone: "ok",
    detail: "One exact copy. Review the delivery options and full total at checkout."
  };
}

export interface CheckoutStage {
  readonly title: string;
  readonly detail: string;
  /** True only when core's authoritative state says the order is paid. */
  readonly paid: boolean;
  /** True while the customer must wait and must not retry (MASTER_SPEC 9.6). */
  readonly awaitingAuthority: boolean;
}

/**
 * The checkout status vocabulary.
 *
 * `PENDING_PAYMENT` is the state this product has to get exactly right (Design
 * Bible 13.3). It is not an error and not a success: core is retrieving the
 * authoritative provider result, and the customer must not retry. There is no
 * `Try again` in that branch, deliberately.
 */
export function checkoutStage(status: CheckoutStatus): CheckoutStage {
  switch (status) {
    case "TAX_PENDING":
      return {
        title: "Working out your total",
        detail:
          "Your items are held while tax is calculated for the address you entered. The final total is not ready yet.",
        paid: false,
        awaitingAuthority: false
      };
    case "READY":
      return {
        title: "Ready to pay",
        detail: "Your total is final. Payment is completed on PayPal.",
        paid: false,
        awaitingAuthority: false
      };
    case "CONSUMED":
      return {
        title: "Order created",
        detail: "Your quote became an order. Payment has not been confirmed yet.",
        paid: false,
        awaitingAuthority: false
      };
    case "PENDING_PAYMENT":
      return {
        title: "Confirming with PayPal",
        detail:
          "Payment has not been confirmed in the order record. Do not pay again. Check status to read the latest result.",
        paid: false,
        awaitingAuthority: true
      };
    case "PAID":
      return {
        title: "Payment confirmed by PayPal",
        detail: "PayPal confirmed the payment to our server. This is the authoritative record.",
        paid: true,
        awaitingAuthority: false
      };
    case "EXPIRED":
      return {
        title: "This checkout expired",
        detail:
          "The server reports that this checkout expired. This status does not establish whether a payment or refund occurred.",
        paid: false,
        awaitingAuthority: false
      };
    case "CANCELLED":
      return {
        title: "This checkout was cancelled",
        detail: "The server reports that this checkout was cancelled. This status does not establish whether a payment or refund occurred.",
        paid: false,
        awaitingAuthority: false
      };
  }
}

export interface HoldNotice {
  readonly title: string;
  readonly detail: string;
  readonly recovery: string;
}

/**
 * The three holds core can write, each with the recovery the customer actually
 * has. `PROVIDER_ADDRESS_MISMATCH` is the one that must never be papered over:
 * core refuses to capture until the address is reconciled, and this surface is
 * forbidden from silently patching the address to make the mismatch go away.
 */
export function holdNotice(reason: HoldReason, paymentRecorded = false): HoldNotice {
  if (reason === "CHECKOUT_RECONCILIATION_REQUIRED") {
    return {
      title: "This order is held while its checkout records are reconciled",
      detail:
        "The provider and reservation records changed at the checkout boundary. The payment status shown above remains the authoritative record.",
      recovery:
        "Do not pay again. We review the order and email you when its inventory and delivery state are reconciled."
    };
  }
  if (reason === "PROVIDER_ADDRESS_MISMATCH") {
    if (paymentRecorded) {
      return {
        title: "The delivery address PayPal returned is not the one you entered",
        detail:
          "Your payment was recorded exactly once, but fulfillment is held because PayPal returned a different delivery address.",
        recovery:
          "Do not pay again. We will email you to reconcile the address before anything ships."
      };
    }
    return {
      title: "The delivery address PayPal returned is not the one you entered",
      detail:
        "This order is held because the delivery addresses differ. We do not change a delivery address for you, and we do not ship to an address you did not type here.",
      recovery:
        "Do not pay again. Ask about this order to resolve the address mismatch."
    };
  }
  if (reason === "SELLER_PROTECTION_INELIGIBLE") {
    return {
      title: "This order is held for a manual check before it ships",
      detail:
        paymentRecorded
          ? "Your payment is confirmed. The payment did not qualify for full seller protection, so the order needs a manual review before shipping."
          : "Payment has not been confirmed. This order needs a manual review of seller protection before shipping.",
      recovery:
        "Nothing is required from you. We email you when the review finishes, and you can ask about the order at any time."
    };
  }
  return {
    title: "This order is held for a manual check before it ships",
    detail:
      paymentRecorded
        ? "Your payment is confirmed. Orders above a set value are reviewed by a person before they are packed."
        : "Payment has not been confirmed. This order needs a manual review before packing.",
    recovery:
      "Nothing is required from you. We email you when the review finishes, and you can ask about the order at any time."
  };
}
